import { useAtom } from "jotai";
import type { ReactNode } from "react";
import { Shirt } from "lucide-react";
import { getPath } from "common/utils/ui/use_svg";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@design-lab/shared/ui/tabs";
import type { DesignSubTab } from "@design-lab/shared/types/tab";
import { designSubTabAtom } from "@design-lab/shared/lib/tab-navigation";

type FrontBackSubTabsProps = {
  title: string;
  number: number;
  testIdPrefix: string;
  frontContent: ReactNode;
  backContent: ReactNode;
};

/**
 * Reusable component for tabs with Front/Back sub-tabs
 * Used in Upload and Design tabs when ab_design_lab_reorder_tabs experiment is enabled
 */
export function FrontBackSubTabs({
  title,
  number,
  testIdPrefix,
  frontContent,
  backContent
}: FrontBackSubTabsProps) {
  const [activeSubTab, setActiveSubTab] = useAtom(designSubTabAtom);
  const spritePath = getPath();

  return (
    <div className="bg-card text-card-foreground flex h-full flex-col gap-4 rounded-xl">
      <h2 className="mt-4 hidden text-xl font-semibold lg:block lg:px-4">
        {number}. {title}
      </h2>
      <Tabs
        value={activeSubTab}
        onValueChange={(value: string) => setActiveSubTab(value as DesignSubTab)}
        className="flex flex-1 flex-col overflow-hidden"
      >
        <div className="mb-2.5 lg:mb-0 lg:px-4">
          <TabsList
            className="flex h-[44px] w-full items-center rounded-lg border border-gray-200 bg-white p-1 text-gray-700 shadow-md [&>*]:flex-1"
            style={{
              backgroundColor: "white",
              borderColor: "#e5e7eb",
              boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)"
            }}
          >
            <TabsTrigger
              data-testid={`${testIdPrefix}-subtab-front`}
              value="front"
              className="h-9 cursor-pointer rounded-md text-gray-700 hover:bg-gray-100 data-[state=active]:!bg-blue-700 data-[state=active]:!text-white data-[state=active]:!shadow-none"
            >
              <Shirt
                className="mr-1 size-5 shrink-0"
                stroke={activeSubTab === "front" ? "#fff" : "#6b7280"}
              />
              <span className="whitespace-nowrap">Front of Shirt</span>
            </TabsTrigger>
            <TabsTrigger
              data-testid={`${testIdPrefix}-subtab-back`}
              value="back"
              className="h-9 cursor-pointer rounded-md text-gray-700 hover:bg-gray-100 data-[state=active]:!bg-blue-700 data-[state=active]:!text-white data-[state=active]:!shadow-none"
            >
              <svg
                className="mr-1 size-5 shrink-0"
                style={{ fill: "none" }}
                aria-label="Back of Shirt"
              >
                <use
                  xlinkHref={`${spritePath}#${activeSubTab === "back" ? "backofshirt-white" : "backofshirt"}`}
                />
              </svg>
              <span className="whitespace-nowrap">Back of Shirt</span>
            </TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="front" className="mt-0 flex-1 overflow-hidden">
          {frontContent}
        </TabsContent>
        <TabsContent value="back" className="mt-0 flex-1 overflow-hidden">
          {backContent}
        </TabsContent>
      </Tabs>
    </div>
  );
}
